import React, { FC, useState } from "react"
import { PageProps, graphql } from "gatsby"
import styled, { keyframes } from "styled-components"

import { AuthorPageQueryQuery } from "@graphqlTypes"

import Cards, { CARDS_VARIANTS } from "@components/Cards/Cards"

import AuthorPageHero from "./components/AuthorPageHero/AuthorPageHero"
import BespokeComponent, { BESPOKE_COMPONENTS } from "@components/lp/BespokeComponent/BespokeComponent"
import Prefooter from "@components/Prefooter/Prefooter"
import SEO from "@components/SEO/SEO"
import TextLink from "@components/TextLink/TextLink"
import AuthorPageStructuredData from "./components/AuthorPageStructuredData/AuthorPageStructuredData"
import useCommonUrlsAndNumbers from "@hooks/useCommonUrlsAndNumbers"

const ShowAllPostsAnim = keyframes`
  0% {
    opacity: 0;
    translate: 0 calc(var(--spacing--XL) * -1);
  }

  100% {
    opacity: 1;
    translate: 0 0;
  }
`

const AuthorPageDIV = styled.div`
  .view-more {
    text-align: center;
    margin-bottom: var(--spacing--XL);
  }

  .Cards--show-all-posts {
    animation: ${ShowAllPostsAnim} 0.75s forwards;
  }
`

export const Head: FC<PageProps<AuthorPageQueryQuery>> = ({
  data: {
    authorData,
    seoImageData,
  }
}) => (
  <SEO
    description=""
    image={seoImageData?.seoImage?.imgixImage?.url || ""}
    permalink={authorData?.permalink || "/"}
    robots="noindex,follow"
    title={`${authorData?.name || ""} - Help Scout`}
  />
)

const AuthorPage: FC<{ data: AuthorPageQueryQuery }> = ({
  data: {
    allContentfulPostCategory,
    authorData,
    customerServicePosts,
    growthPosts,
    newsletterFullWidthCtaData,
    productPosts,
  }
}) => {
  if (!authorData) return null

  const [showAll, setShowAll] = useState<boolean>(false)
  const {
    demoUrl,
    signupPlusUrl,
    trialDaysLength,
  } = useCommonUrlsAndNumbers()

  const {
    allPosts,
    featuredPosts,
    name,
  } = authorData

  const cardsTitle = `Posts by ${name === "Help Scout" ? name : name?.split(" ")[0]}`
  const hasFeaturedPosts = featuredPosts && featuredPosts.length > 0

  const handleClickShowAll = () => {
    setShowAll(true)
  }

  return (
    <AuthorPageDIV>
      <AuthorPageHero {...authorData} />
      {hasFeaturedPosts && (
        <div className="container">
          <Cards
            items={featuredPosts}
            title={cardsTitle}
            variant={
              featuredPosts.length === 2 ?
                CARDS_VARIANTS.LARGE :
                CARDS_VARIANTS.WITH_FEATURED
            }
          />
        </div>
      )}
      {allPosts && allPosts.length > 0 && (
        <div className="container">
          <Cards
            items={allPosts.slice(0, 9)}
            title={!hasFeaturedPosts ? cardsTitle : undefined}
            variant={
              allPosts.length === 1 ?
                CARDS_VARIANTS.WITH_FEATURED :
                allPosts.length === 2 ?
                  CARDS_VARIANTS.LARGE :
                  CARDS_VARIANTS.DEFAULT
            }
          />
        </div>
      )}
      {/* VIEW MORE */}
      {allPosts && allPosts.length > 9 && (
        <div className="container">
          {!showAll && (
            <div className="grid view-more">
              <div className="column">
                <TextLink
                  onClick={handleClickShowAll}
                  rotateIcon
                >
                  Show {allPosts.length - 9} more posts
                </TextLink>
              </div>
            </div>
          )}
          {showAll && (
            <Cards
              className="Cards--show-all-posts"
              items={allPosts.slice(9)}
            />
          )}
        </div>
      )}
      <BespokeComponent {...newsletterFullWidthCtaData} />
      {allContentfulPostCategory && (
        <div className="container">
          <h2 className="text-align--center">Discover the latest in...</h2>
        </div>
      )}
      {allContentfulPostCategory && allContentfulPostCategory.nodes.map((category) => {
        const postsToUse = (() => {
          switch (category.slug) {
            case "customer-service":
              return customerServicePosts.nodes

            case "growth":
              return growthPosts.nodes

            case "product":
              return productPosts.nodes

            default:
              return null
          }
        })()

        if (!postsToUse || postsToUse.length === 0) return null

        return (
          <div className="container" key={category.slug}>
            <Cards
              items={postsToUse}
              moreLink={category.permalink || undefined}
              moreText="View All Posts"
              showSubcategory
              title={category.title || undefined}
            />
          </div>
        )
      })}
      <Prefooter
        title="Try Help Scout for free"
        bodyText={{ bodyText: "A better experience for your customers, fewer headaches for your team. You’ll be set up in minutes" }}
        button1Label={`Free ${trialDaysLength}-day Trial`}
        button1Url={signupPlusUrl}
        button2Label="Get a Demo"
        button2Url={demoUrl}
      />
      <AuthorPageStructuredData
        name={name}
        recentPosts={featuredPosts || allPosts?.slice(0, 3)}
      />
    </AuthorPageDIV>
  )
}

export const pageQuery = graphql`
  fragment AuthorFragment on ContentfulPerson {
    name
    twitter
    url
    permalink
    bio {
      childMarkdownRemark {
        html
      }
    }
    image {
      imgixImage {
        gatsbyImageData(
          width: 500
          height: 500
        )
      }
    }
    featuredPosts {
      ...CardFragment
    }
    allPosts {
      ...CardFragment
    }
  }

  query AuthorPageQuery($id: String!) {
    authorData: contentfulPerson(id: {eq: $id }){
      ...AuthorFragment
    }
    seoImageData: contentfulPostCategory(slug: {eq: "all"}) {
      seoImage {
        imgixImage {
          url
        }
      }
    }
    allContentfulPostCategory(
      filter: {slug: {ne: "all"}}
    ) {
      nodes {
        title
        slug
        permalink
      }
    }
    customerServicePosts: allContentfulPost(
      filter: {category: {elemMatch: {slug: {eq: "customer-service"}}}}
      sort: {date: DESC}
      limit: 3
    ) {
      nodes {
        ...CardFragment
      }
    }
    growthPosts: allContentfulPost(
      filter: {category: {elemMatch: {slug: {eq: "growth"}}}}
      sort: {date: DESC}
      limit: 3
    ) {
      nodes {
        ...CardFragment
      }
    }
    productPosts: allContentfulPost(
      filter: {category: {elemMatch: {slug: {eq: "product"}}}}
      sort: {date: DESC}
      limit: 3
    ) {
      nodes {
        ...CardFragment
      }
    }
    newsletterFullWidthCtaData: contentfulLpBespokeComponent(contentful_id: {eq:"1TbA9ST7Ak2LR1R3nzGdzU"}) {
      ...BespokeComponentFragment
    }
  }
`

export default AuthorPage
